import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Row, Col, Modal } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import Webcam from 'react-webcam';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './HR.css';

// Réparer l'icône par défaut de Leaflet qui casse avec Webpack/React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export default function PointagePage() {
  const user = useSelector(s => s.AuthReducer?.user);
  const [gpsStatus, setGpsStatus]         = useState('loading');
  const [gpsCoords, setGpsCoords]         = useState(null);
  const [distance, setDistance]           = useState(null);
  const [todayData, setTodayData]         = useState({ pointages: [], totalMinutes: 0, pairs: [], currentlyIn: false });
  const [todayShift, setTodayShift]       = useState(null);
  const [loading, setLoading]             = useState(false);
  const [successMsg, setSuccessMsg]       = useState('');
  const [errorMsg, setErrorMsg]           = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [reportData, setReportData]       = useState([]);
  const [reportMonth, setReportMonth]     = useState(new Date().toISOString().slice(0,7));

  // 🔥 Nouveaux états pour le clavier numérique et selfie
  const [enteredMatricule, setEnteredMatricule] = useState('');
  const [activeMatricule, setActiveMatricule]   = useState(null);
  const [isAuthenticated, setIsAuthenticated]   = useState(false);
  const [step, setStep]                         = useState('keypad'); // 'keypad', 'selfie', 'pointage'
  const [capturedPhoto, setCapturedPhoto]       = useState(null);
  const webcamRef = React.useRef(null);

  const token = localStorage.getItem('token');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const headers = useMemo(() => ({ headers: { Authorised: token } }), [token]);

  // Charger les pointages du jour
  const fetchToday = useCallback(async () => {
    if (!activeMatricule) return;
    try {
      const { data } = await axios.get(`/api/hr/pointage/today-by-matricule/${activeMatricule}`, headers);
      setTodayData(data);
    } catch { /* silent */ }
  }, [activeMatricule, headers]);

  const [employee, setEmployee]           = useState(null);

  // Charger le profil employé
  const fetchEmployee = useCallback(async () => {
    if (!activeMatricule) return;
    try {
      const { data } = await axios.get(`/api/employees/${activeMatricule}`, headers);
      if (data.employee) {
        setEmployee(data.employee);
        setIsAuthenticated(true);
        // On ne change le step que si on est au début
        setStep(current => current === 'keypad' ? 'selfie' : current);
      } else {
        setErrorMsg('Matricule inconnu');
        setActiveMatricule(null);
      }
    } catch { 
      setErrorMsg('Erreur lors de la recherche du matricule');
      setActiveMatricule(null);
    }
  }, [activeMatricule, headers]);

  // Charger le planning du jour
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchShift = useCallback(async () => {
    if (!employee) return;
    try {
      const today = new Date();
      const dayOfWeek = today.getDay() === 0 ? 6 : today.getDay() - 1;
      const d = new Date(today);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + 4 - (d.getDay() || 7));
      const yearStart = new Date(d.getFullYear(), 0, 1);
      const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
      const week = `${d.getFullYear()}-W${String(weekNo).padStart(2,'0')}`;
      const { data } = await axios.get(`/api/hr/schedule?week=${week}`, headers);
      const mySchedule = data.find(s => s.employee?._id === employee._id || s.employee === employee._id);
      if (mySchedule) {
        const shift = mySchedule.shifts?.find(s => s.dayOfWeek === dayOfWeek && !s.isOff);
        setTodayShift(shift || null);
      }
    } catch { /* silent */ }
  }, [employee]);


  // Obtenir la position GPS
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsStatus('no-loc');
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      pos => {
        const { latitude, longitude } = pos.coords;
        setGpsCoords({ lat: latitude, lng: longitude });
        // La validation réelle est faite côté serveur
        // Ici on fait juste un pré-calcul si on a les coords de la location
        setGpsStatus('valid'); // Sera validé au moment du pointage
      },
      () => setGpsStatus('no-loc'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  useEffect(() => {
    fetchEmployee();
    fetchToday();
    fetchShift();
    const interval = setInterval(fetchToday, 60000);
    return () => clearInterval(interval);
  }, [fetchEmployee, fetchToday, fetchShift]);


  // Pointer entrée ou sortie
  const handlePointage = async (type) => {
    if (!gpsCoords && gpsStatus !== 'no-loc') {
      setErrorMsg('Position GPS non encore disponible. Veuillez patienter...');
      return;
    }
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const { data } = await axios.post('/api/hr/pointage', {
        type,
        gps: gpsCoords,
        matricule: activeMatricule,
        photo: capturedPhoto // On envoie la photo capturée
      }, { headers: { Authorised: token } });

      if (data.distance !== null) setDistance(data.distance);
      if (data.gpsValid === false && data.distance > 0) {
        setGpsStatus('invalid');
      }
      setSuccessMsg(data.message);
      await fetchToday();
      
      // Auto-reset après succès pour le prochain employé
      setTimeout(() => {
        handleReset();
      }, 5000);

    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setEmployee(null);
    setActiveMatricule(null);
    setEnteredMatricule('');
    setIsAuthenticated(false);
    setStep('keypad');
    setCapturedPhoto(null);
    setSuccessMsg('');
    setErrorMsg('');
    setTodayData({ pointages: [], totalMinutes: 0, pairs: [], currentlyIn: false });
  };

  const handleKeypadPress = (val) => {
    if (val === 'DEL') {
      setEnteredMatricule(prev => prev.slice(0, -1));
    } else if (val === 'CLR') {
      setEnteredMatricule('');
    } else {
      if (enteredMatricule.length < 10) {
        setEnteredMatricule(prev => prev + val);
      }
    }
  };

  const handleKeypadSubmit = () => {
    if (enteredMatricule.length >= 3) {
      setActiveMatricule(enteredMatricule);
    } else {
      setErrorMsg('Matricule trop court');
    }
  };

  const capturePhoto = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedPhoto(imageSrc);
  }, [webcamRef]);

  const confirmPhoto = () => {
    if (capturedPhoto) {
      setStep('pointage');
    }
  };

  // Rapport mensuel
  const fetchReport = async () => {
    try {
      const start = `${reportMonth}-01`;
      const lastDay = new Date(reportMonth.split('-')[0], reportMonth.split('-')[1], 0).getDate();
      const end = `${reportMonth}-${lastDay}`;
      const { data } = await axios.get(`/api/hr/pointage/report?employeeId=${employee._id}&startDate=${start}&endDate=${end}`, headers);
      setReportData(data);
      setShowDetailModal(true);
    } catch { /* silent */ }
  };

  const formatTime = (date) => date ? new Date(date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '--:--';
  const formatMinutes = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return h > 0 ? `${h}h${String(m).padStart(2,'0')}` : `${m}min`;
  };

  const today = new Date();
  const dayName = DAYS[today.getDay() === 0 ? 6 : today.getDay() - 1];

  // Composant pour recentrer la carte quand les coords changent
  function ChangeView({ gps, workLocs }) {
    const map = useMap();
    useEffect(() => {
      const bounds = L.latLngBounds();
      if (gps?.lat && gps?.lng) bounds.extend([gps.lat, gps.lng]);
      if (workLocs && workLocs.length > 0) {
        workLocs.forEach(loc => {
          if (loc.gps?.lat && loc.gps?.lng) bounds.extend([loc.gps.lat, loc.gps.lng]);
        });
      }
      
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50] });
      } else if (gps?.lat && gps?.lng) {
        map.setView([gps.lat, gps.lng], 16);
      }
    }, [gps, workLocs, map]);
    return null;
  }

  // Calculer la distance la plus proche
  const minDistance = useMemo(() => {
    if (!gpsCoords || !employee?.assignedLocations?.length) return null;
    let min = Infinity;
    employee.assignedLocations.forEach(loc => {
      if (loc.gps?.lat && loc.gps?.lng) {
        const d = haversineDistance(gpsCoords.lat, gpsCoords.lng, loc.gps.lat, loc.gps.lng);
        if (d < min) min = d;
      }
    });
    return min === Infinity ? null : Math.round(min);
  }, [gpsCoords, employee]);

  // Vérifier si on est dans les horaires (1h avant debut, jusqu'à la fin de journée)
  const isTimeValid = useMemo(() => {
    if (!todayShift) return false;
    const now = new Date();
    const currentMins = now.getHours() * 60 + now.getMinutes();
    const startMins = timeToMinutes(todayShift.startTime);
    
    // Autoriser 60 minutes avant le début du shift
    return currentMins >= (startMins - 60);
  }, [todayShift]);

  const isGpsValid = minDistance !== null && minDistance <= 200;
  
  const lastPointage = todayData.pointages?.[todayData.pointages.length - 1];
  const hasBreak = !!(employee?.assignedLocations?.[0]?.requireBreak); 

  const canEnter      = !lastPointage;
  const canStartBreak = hasBreak && lastPointage?.type === 'entree';
  const canEndBreak   = hasBreak && lastPointage?.type === 'debut_pause';
  const canExit       = (hasBreak && lastPointage?.type === 'fin_pause') || (!hasBreak && lastPointage?.type === 'entree');
  
  // Le bouton est activé SEULEMENT si GPS est valide ET qu'on est dans le créneau horaire
  const isPointageAllowed = isGpsValid && isTimeValid;
  const btnDisabled = loading || gpsStatus === 'loading' || !isPointageAllowed;


  // Calculer la distance Haversine côté client pour l'affichage
  function haversineDistance(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const toRad = d => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  function timeToMinutes(t) {
    if (!t) return 0;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  }

  if (step === 'keypad') {
    return (
      <div className="keypad-container">
        <div className="keypad-card">
          <h2 style={{ color: '#4318FF', fontWeight: 800, marginBottom: '20px' }}>🔐 Identification Employé</h2>
          <p style={{ color: '#718096', marginBottom: '30px' }}>Veuillez saisir votre matricule pour pointer</p>
          
          <div className="matricule-display">
            {enteredMatricule || '----'}
          </div>

          <div className="numeric-keypad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'CLR', 0, 'DEL'].map((k, i) => (
              <button 
                key={i} 
                className={`keypad-btn ${typeof k === 'string' ? 'action' : ''}`}
                onClick={() => handleKeypadPress(k)}
              >
                {k}
              </button>
            ))}
          </div>

          <button className="keypad-submit" onClick={handleKeypadSubmit} disabled={enteredMatricule.length < 3}>
            VALIDER
          </button>

          {errorMsg && <div className="keypad-error" style={{ marginTop: '20px', color: '#ee5d50', fontWeight: 'bold' }}>{errorMsg}</div>}
        </div>
      </div>
    );
  }

  if (step === 'selfie') {
    return (
      <div className="keypad-container">
        <div className="keypad-card" style={{ maxWidth: '500px' }}>
          <h2 style={{ color: '#4318FF', fontWeight: 800, marginBottom: '10px' }}>📸 Selfie de Vérification</h2>
          <p style={{ color: '#718096', marginBottom: '20px' }}>{employee?.name}, veuillez prendre une photo pour valider votre identité.</p>
          
          <div className="webcam-wrap" style={{ borderRadius: '16px', overflow: 'hidden', marginBottom: '20px', border: '3px solid #4318FF', background: '#000', minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {!capturedPhoto ? (
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                width="100%"
                videoConstraints={{ facingMode: "user" }}
              />
            ) : (
              <img src={capturedPhoto} alt="Capture" style={{ width: '100%' }} />
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            {!capturedPhoto ? (
              <button className="keypad-submit" onClick={capturePhoto}>
                PRENDRE LA PHOTO
              </button>
            ) : (
              <>
                <button className="hr-btn hr-btn-secondary" style={{ flex: 1 }} onClick={() => setCapturedPhoto(null)}>
                  REPRENDRE
                </button>
                <button className="keypad-submit" style={{ flex: 2, margin: 0 }} onClick={confirmPhoto}>
                  CONFIRMER
                </button>
              </>
            )}
          </div>
          
          <button 
            className="hr-btn hr-btn-link" 
            style={{ marginTop: '20px', color: '#718096', background: 'none', border: 'none', textDecoration: 'underline' }}
            onClick={handleReset}
          >
            Annuler
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hr-page">
      {/* Header */}
      <div className="hr-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <h1 style={{ marginBottom: '8px' }}>👤 {employee?.name}</h1>
            <div style={{ background: 'rgba(255,255,255,0.15)', padding: '6px 12px', borderRadius: '8px', display: 'inline-block', marginBottom: '8px' }}>
              <span style={{ fontWeight: 800, color: '#fff', fontSize: '1rem' }}>
                📍 {employee?.assignedLocations?.length > 0 
                  ? employee.assignedLocations.map(l => l.name).join(' / ') 
                  : 'Aucun emplacement assigné'}
              </span>
            </div>
            <p style={{ margin: 0, opacity: 0.9 }}>{dayName} {today.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
          <button className="hr-btn hr-btn-secondary" onClick={handleReset} style={{ backgroundColor: '#718096', border: 'none', color: 'white' }}>
            🚪 Terminer / Changer
          </button>
        </div>
      </div>

      <Row>
        {/* Colonne gauche — Pointage */}
        <Col lg={4} md={6}>
          <div className="hr-card text-center">
            <div className="hr-card-title">
              <span className="icon">⏰</span> Pointage du Jour
            </div>

            {/* Statut GPS */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              {gpsStatus === 'loading' && (
                <div className="gps-status loading"><div className="gps-dot" /> Localisation en cours...</div>
              )}
              {gpsStatus === 'valid' && (
                <div className={`gps-status ${isGpsValid ? 'valid' : 'invalid'}`}>
                  <div className="gps-dot" />
                  {minDistance !== null ? `À ${minDistance}m du site ${isGpsValid ? '✓' : '(Trop loin)'}` : 'Position détectée ✓'}
                </div>
              )}
              {gpsStatus === 'no-loc' && (
                <div className="gps-status invalid">⚠ GPS non disponible</div>
              )}

            </div>

            {/* Carte Interactive */}
            <div style={{ height: '220px', width: '100%', borderRadius: 12, overflow: 'hidden', marginBottom: 20, border: '1px solid #e0e7ff' }}>
              <MapContainer 
                center={(() => {
                  const locs = Array.isArray(user?.assignedLocations) ? user.assignedLocations : (user?.assignedLocations ? [user.assignedLocations] : []);
                  return locs[0]?.gps?.lat ? [locs[0].gps.lat, locs[0].gps.lng] : [36.8065, 10.1815];
                })()} 
                zoom={14} 
                style={{ height: '100%', width: '100%' }}
                zoomControl={false}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {gpsCoords && <Marker position={[gpsCoords.lat, gpsCoords.lng]} />}
                
                {/* Afficher tous les lieux assignés de l'employé */}
                {(() => {
                  const locs = Array.isArray(employee?.assignedLocations) ? employee.assignedLocations : [];
                  return locs.map(loc => loc.gps?.lat && (
                    <React.Fragment key={loc._id}>
                      <Marker position={[loc.gps.lat, loc.gps.lng]} icon={L.divIcon({ className: 'custom-div-icon', html: `<div style="background-color:#4318FF;width:12px;height:12px;border-radius:50%;border:2px solid white;"></div>`, iconSize: [12, 12], iconAnchor: [6, 6] })} />
                      <Circle 
                        center={[loc.gps.lat, loc.gps.lng]} 
                        radius={200}
                        pathOptions={{ color: isGpsValid ? '#05cd99' : '#4318FF', fillColor: isGpsValid ? '#05cd99' : '#4318FF', fillOpacity: 0.1 }}
                      />
                    </React.Fragment>
                  ));
                })()}


                {gpsCoords && <ChangeView gps={gpsCoords} workLocs={employee?.assignedLocations || []} />}
              </MapContainer>
            </div>

            {/* Boutons pointage dynamique */}
            <div className="pointage-btn-wrap">
              {canEnter && (
                <button
                  className={`pointage-btn entree ${btnDisabled ? 'disabled' : ''}`}
                  onClick={() => handlePointage('entree')}
                  disabled={btnDisabled}
                >
                  <span className="btn-icon">🟢</span>
                  <span className="btn-label">ENTRÉE</span>
                  {todayShift && <span style={{ fontSize: '0.7rem', opacity: 0.85 }}>Prévu {todayShift.startTime}</span>}
                </button>
              )}
              
              {canStartBreak && (
                <button
                  className={`pointage-btn pause ${btnDisabled ? 'disabled' : ''}`}
                  onClick={() => handlePointage('debut_pause')}
                  disabled={btnDisabled}
                  style={{ background: 'linear-gradient(135deg, #ffb547, #f39c12)' }}
                >
                  <span className="btn-icon">☕</span>
                  <span className="btn-label">DÉBUT PAUSE</span>
                </button>
              )}

              {canEndBreak && (
                <button
                  className={`pointage-btn pause ${btnDisabled ? 'disabled' : ''}`}
                  onClick={() => handlePointage('fin_pause')}
                  disabled={btnDisabled}
                  style={{ background: 'linear-gradient(135deg, #4318FF, #6b46ff)' }}
                >
                  <span className="btn-icon">🔄</span>
                  <span className="btn-label">FIN PAUSE</span>
                </button>
              )}

              {canExit && (
                <button
                  className={`pointage-btn sortie ${btnDisabled ? 'disabled' : ''}`}
                  onClick={() => handlePointage('sortie')}
                  disabled={btnDisabled}
                >
                  <span className="btn-icon">🔴</span>
                  <span className="btn-label">SORTIE</span>
                  {todayShift && <span style={{ fontSize: '0.7rem', opacity: 0.85 }}>Fin prévue {todayShift.endTime}</span>}
                </button>
              )}
            </div>

            {successMsg && <div style={{ background: '#e6fff6', color: '#05cd99', padding: '10px 16px', borderRadius: 10, fontWeight: 600, fontSize: '0.9rem' }}>{successMsg}</div>}
            {errorMsg   && <div style={{ background: '#fff0f0', color: '#ee5d50', padding: '10px 16px', borderRadius: 10, fontWeight: 600, fontSize: '0.9rem' }}>{errorMsg}</div>}
            
            {!isPointageAllowed && !loading && gpsStatus === 'valid' && (
              <div style={{ marginTop: 12, padding: 12, background: '#fff9e6', color: '#ff9900', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600 }}>
                {!todayShift 
                  ? "🛑 Aucun planning prévu aujourd'hui." 
                  : !isTimeValid 
                    ? `⏳ Trop tôt. Pointage autorisé à partir de ${(() => {
                        const [h, m] = todayShift.startTime.split(':').map(Number);
                        const d = new Date(); d.setHours(h-1, m, 0);
                        return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                      })()}.`
                    : (minDistance === null) 
                      ? "📍 Recherche de votre position par rapport au site..."
                      : minDistance > 200
                        ? `📍 Vous êtes à ${minDistance}m. Rapprochez-vous de ${minDistance - 200}m pour pouvoir pointer.`
                        : "📍 Vous êtes dans la zone autorisée. Vous pouvez pointer."
                }
              </div>
            )}


          </div>

          {/* Planning du jour */}
          {todayShift && (
            <div className="hr-card">
              <div className="hr-card-title"><span className="icon">📅</span> Planning Aujourd'hui</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
                <div>
                  <div style={{ fontSize: '0.78rem', color: '#718096', fontWeight: 600 }}>ARRIVÉE PRÉVUE</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f1535' }}>{todayShift.startTime}</div>
                </div>
                <div style={{ fontSize: '1.5rem', color: '#cbd5e0' }}>→</div>
                <div>
                  <div style={{ fontSize: '0.78rem', color: '#718096', fontWeight: 600 }}>DÉPART PRÉVU</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f1535' }}>{todayShift.endTime}</div>
                </div>
              </div>
            </div>
          )}
        </Col>

        {/* Colonne droite — Timeline + Stats */}
        <Col lg={8} md={6}>
          {/* Stats du jour */}
          <div className="hr-stat-grid" style={{ marginBottom: 20 }}>
            <div className="hr-stat-card success">
              <div className="stat-label">Heures Travaillées</div>
              <div className="stat-value">{formatMinutes(todayData.totalMinutes)}</div>
              <div className="stat-sub">Aujourd'hui</div>
            </div>
            <div className="hr-stat-card info">
              <div className="stat-label">Statut</div>
              <div className="stat-value" style={{ fontSize: '1.3rem' }}>
                {todayData.currentlyIn ? '🟢 En poste' : todayData.totalMinutes > 0 ? '✅ Sorti' : '⏳ Pas encore'}
              </div>
            </div>
            <div className="hr-stat-card">
              <div className="stat-label">Pointages</div>
              <div className="stat-value">{todayData.pointages?.length || 0}</div>
              <div className="stat-sub">Entrées/Sorties</div>
            </div>
          </div>

          {/* Timeline */}
          <div className="hr-card">
            <div className="hr-card-title"><span className="icon">📋</span> Historique du Jour</div>
            {todayData.pointages?.length > 0 ? (
              <div className="pointage-timeline">
                {todayData.pointages.map((p, i) => (
                  <div key={i} className="pointage-timeline-item">
                    <div className="time-badge">{formatTime(p.timestamp)}</div>
                    <span className={`type-badge ${p.type}`}>
                      {p.type === 'entree' ? '↗ Entrée' : '↙ Sortie'}
                    </span>
                    {p.gpsValid !== undefined && (
                      <span style={{ fontSize: '0.75rem', color: p.gpsValid ? '#05cd99' : '#ee5d50' }}>
                        {p.gpsValid ? '📍 GPS ✓' : '📍 GPS ✗'}
                      </span>
                    )}
                    {p.type === 'entree' && (
                      <span className={`status-badge ${p.status}`}>
                        {p.status === 'present' ? '✅ À l\'heure' : p.status === 'retard' ? `⚠ Retard ${p.delayMinutes}min` : '❌ Absent'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#a0aec0', padding: '30px 0' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🕐</div>
                <p>Aucun pointage aujourd'hui</p>
              </div>
            )}
          </div>

          {/* Rapport mensuel */}
          <div className="hr-card">
            <div className="hr-card-title"><span className="icon">📊</span> Rapport Mensuel</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="month"
                value={reportMonth}
                onChange={e => setReportMonth(e.target.value)}
                className="hr-form-control"
                style={{ maxWidth: 200 }}
              />
              <button className="hr-btn hr-btn-primary" onClick={fetchReport}>
                📈 Voir mon rapport
              </button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Modal Rapport Mensuel */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg,#4318FF,#6b46ff)', color: 'white' }}>
          <Modal.Title>📊 Rapport — {reportMonth}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reportData.length === 0 ? (
            <p className="text-center text-muted py-4">Aucun pointage trouvé pour cette période</p>
          ) : (
            <>
              <div className="hr-stat-grid" style={{ marginBottom: 20 }}>
                <div className="hr-stat-card success">
                  <div className="stat-label">Jours Présents</div>
                  <div className="stat-value">{reportData.filter(r => r.status !== 'absent').length}</div>
                </div>
                <div className="hr-stat-card warning">
                  <div className="stat-label">Retards</div>
                  <div className="stat-value">{reportData.filter(r => r.status === 'retard').length}</div>
                </div>
                <div className="hr-stat-card danger">
                  <div className="stat-label">Absences</div>
                  <div className="stat-value">{reportData.filter(r => r.status === 'absent').length}</div>
                </div>
                <div className="hr-stat-card">
                  <div className="stat-label">Total Heures</div>
                  <div className="stat-value" style={{ fontSize: '1.4rem' }}>
                    {formatMinutes(reportData.reduce((s, r) => s + (r.minutesTravaillees || 0), 0))}
                  </div>
                </div>
                <div className="hr-stat-card danger">
                  <div className="stat-label">Pénalités (H)</div>
                  <div className="stat-value">{reportData.reduce((s, r) => s + (r.penaltyHours || 0), 0)}h</div>
                </div>
                <div className="hr-stat-card warning">
                  <div className="stat-label">Pénalités Pause</div>
                  <div className="stat-value">{reportData.reduce((s, r) => s + (r.penaltyMinutes || 0), 0)}m</div>
                  <div className="stat-sub">Défalqué du salaire</div>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="schedule-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Entrée</th>
                      <th>Sortie</th>
                      <th>Durée</th>
                      <th>Statut</th>
                      <th>Retard</th>
                      <th>Sortie Ant.</th>
                      <th>Pén. Pause</th>
                      <th>Pénalité H</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((r, i) => (
                      <tr key={i}>
                        <td>{new Date(r.day).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                        <td>{r.entree ? formatTime(r.entree) : '—'}</td>
                        <td>{r.sortie ? formatTime(r.sortie) : '—'}</td>
                        <td><strong>{r.minutesTravaillees > 0 ? formatMinutes(r.minutesTravaillees) : '—'}</strong></td>
                        <td><span className={`status-badge ${r.status}`}>
                          {r.status === 'present' ? '✅ Présent' : r.status === 'retard' ? '⚠ Retard' : '❌ Absent'}
                        </span></td>
                        <td>{r.delayMinutes > 0 ? `${r.delayMinutes} min` : '—'}</td>
                        <td>{r.earlyExitMinutes > 0 ? `${r.earlyExitMinutes} min` : '—'}</td>
                        <td style={{ color: r.penaltyMinutes > 0 ? '#ffb547' : 'inherit' }}>
                          {r.penaltyMinutes > 0 ? `-${r.penaltyMinutes} min` : '—'}
                        </td>
                        <td style={{ color: r.penaltyHours > 0 ? '#ee5d50' : 'inherit', fontWeight: r.penaltyHours > 0 ? 'bold' : 'normal' }}>
                          {r.penaltyHours > 0 ? `-${r.penaltyHours}h` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="hr-btn hr-btn-secondary" onClick={() => setShowDetailModal(false)}>Fermer</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
